import React, { useEffect, useState } from "react";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import CustomSelect from "components/CustomSelect";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import GoalButton from "layouts/goals/components/GoalButton";
import { NO_AUTOFILL_PROPS } from "layouts/account/passwordInputProps";
import { USER_MANAGEMENT_MESSAGES, userSelectStyles } from "../userManagement.constants";

function FieldLabel({ children }) {
  return (
    <SuiTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
      {children}
    </SuiTypography>
  );
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AdminUserFormModal({
  open,
  onClose,
  onSave,
  isSubmitting,
  initialUser,
  isSelf,
  roles,
  teamOptions,
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roleId, setRoleId] = useState(null);
  const [teamIds, setTeamIds] = useState([]);
  const [localError, setLocalError] = useState(null);

  const isEdit = !!initialUser;
  const roleOptions = roles.map((role) => ({ value: role.id, label: role.name }));

  useEffect(() => {
    setName(initialUser?.name || "");
    setEmail(initialUser?.email || "");
    setPassword("");
    setRoleId(initialUser?.fk_role_id ?? null);
    setTeamIds(initialUser ? initialUser.teams.map((team) => team.id) : []);
    setLocalError(null);
  }, [initialUser, open]);

  const handleSaveClick = async () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName) return setLocalError(USER_MANAGEMENT_MESSAGES.nameRequired);
    if (!isEdit && !EMAIL_PATTERN.test(trimmedEmail)) {
      return setLocalError(USER_MANAGEMENT_MESSAGES.emailRequired);
    }
    if ((!isEdit || password) && password.length < 8) {
      return setLocalError(USER_MANAGEMENT_MESSAGES.passwordRequired);
    }
    if (!roleId) return setLocalError(USER_MANAGEMENT_MESSAGES.roleRequired);
    if (!teamIds.length) return setLocalError(USER_MANAGEMENT_MESSAGES.teamsRequired);

    const body = { name: trimmedName, fk_role_id: roleId, team_ids: teamIds };
    if (!isEdit) body.email = trimmedEmail;
    if (password) body.password = password;
    await onSave(body);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmitting && onClose()}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { overflow: "visible" } }}
    >
      <DialogTitle>{isEdit ? "Edit User" : "Add User"}</DialogTitle>
      <DialogContent sx={{ overflow: "visible" }}>
        {localError && (
          <SuiTypography variant="caption" color="error" display="block" mb={1}>
            {localError}
          </SuiTypography>
        )}
        <SuiBox mb={2}>
          <FieldLabel>Name</FieldLabel>
          <SuiInput
            name="new-user-name"
            autoComplete="off"
            placeholder="Full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </SuiBox>
        <SuiBox mb={2}>
          <FieldLabel>Email</FieldLabel>
          <SuiInput
            name="new-user-email"
            autoComplete="off"
            type="email"
            placeholder="name@company.com"
            value={email}
            disabled={isEdit}
            onChange={(e) => setEmail(e.target.value)}
          />
        </SuiBox>
        {!isSelf && (
          <SuiBox mb={2}>
            <FieldLabel>{isEdit ? "New password (optional)" : "Password"}</FieldLabel>
            <SuiInput
              name="new-user-password"
              {...NO_AUTOFILL_PROPS}
              type="password"
              placeholder={
                isEdit ? "Leave blank to keep current password" : "At least 8 characters"
              }
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </SuiBox>
        )}
        <SuiBox mb={2}>
          <FieldLabel>Role</FieldLabel>
          <CustomSelect
            placeholder="Select role"
            options={roleOptions}
            value={roleOptions.find((option) => option.value === roleId) || null}
            onChange={(option) => setRoleId(option.value)}
            customStyles={userSelectStyles}
            isDisabled={isSelf}
            menuPortalTarget={document.body}
          />
          {isSelf && (
            <SuiTypography variant="caption" color="secondary">
              You can&apos;t change your own role.
            </SuiTypography>
          )}
        </SuiBox>
        <SuiBox>
          <FieldLabel>Teams</FieldLabel>
          <CustomSelect
            placeholder="Select one or more teams"
            options={teamOptions}
            value={teamOptions.filter((option) => teamIds.includes(option.value))}
            onChange={(options) => setTeamIds((options || []).map((option) => option.value))}
            customStyles={userSelectStyles}
            isMulti
            menuPortalTarget={document.body}
          />
        </SuiBox>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <GoalButton variant="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </GoalButton>
        <GoalButton
          variant="primary"
          icon={isEdit ? "save" : "person_add"}
          loading={isSubmitting}
          onClick={handleSaveClick}
        >
          {isEdit ? "Save changes" : "Add user"}
        </GoalButton>
      </DialogActions>
    </Dialog>
  );
}

export default AdminUserFormModal;
